import { useState, useEffect } from 'react'

// =============================================================================
// Toast Types
// =============================================================================

export type ToastType = 'default' | 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  type: ToastType
  title: string
  description?: string
  duration?: number
}

interface ToastOptions {
  title: string
  description?: string
  type?: ToastType
  duration?: number
}

interface ToastFunction {
  (options: ToastOptions): string
  success: (title: string, description?: string) => string
  error: (title: string, description?: string) => string
  warning: (title: string, description?: string) => string
  info: (title: string, description?: string) => string
}

// =============================================================================
// Module-level global state (shared across all hook instances)
// =============================================================================

let toastCount = 0
let memoryState: { toasts: Toast[] } = { toasts: [] }
const listeners = new Set<() => void>()

function emitChange() {
  listeners.forEach((listener) => listener())
}

function addToast(options: ToastOptions): string {
  const id = `toast-${++toastCount}`
  const duration = options.duration ?? (options.type === 'error' ? 8000 : 5000)
  const toastItem: Toast = {
    id,
    type: options.type || 'default',
    title: options.title,
    description: options.description,
    duration,
  }

  memoryState = { toasts: [...memoryState.toasts, toastItem] }
  emitChange()

  if (duration > 0) {
    setTimeout(() => {
      removeToast(id)
    }, duration)
  }

  return id
}

function removeToast(id: string) {
  memoryState = { toasts: memoryState.toasts.filter((t) => t.id !== id) }
  emitChange()
}

// Standalone toast function (usable outside React components)
export const toast: ToastFunction = Object.assign(
  (options: ToastOptions) => addToast(options),
  {
    success: (title: string, description?: string) =>
      addToast({ title, description, type: 'success' }),
    error: (title: string, description?: string) =>
      addToast({ title, description, type: 'error' }),
    warning: (title: string, description?: string) =>
      addToast({ title, description, type: 'warning' }),
    info: (title: string, description?: string) =>
      addToast({ title, description, type: 'info' }),
  }
)

// =============================================================================
// useToast Hook (subscribes to global state)
// =============================================================================

export function useToast() {
  const [state, setState] = useState(memoryState)

  useEffect(() => {
    const listener = () => setState({ ...memoryState })
    listeners.add(listener)
    return () => { listeners.delete(listener) }
  }, [])

  return {
    toasts: state.toasts,
    toast,
    removeToast,
  }
}
